import { SHOT_LANDING_LABELS } from '@golf/common';
import type { ShotLanding } from '@golf/common';
import { ChevronDown, GraduationCap, History, Lightbulb } from 'lucide-react';
import type { ReactNode } from 'react';
import { Etape } from './Etape.tsx';

/** Un club tel que la carte le montre.
 *
 *  Volontairement pauvre : un identifiant, un nom, de quoi teinter la tuile,
 *  et les deux chiffres qu'on lit. Le parcours et le practice ne stockent pas
 *  leurs clubs de la même façon — l'un travaille sur un sac classé par
 *  distance, l'autre sur les clubs d'une séance avec leurs statistiques du
 *  jour — et c'est très bien : chacun traduit vers cette forme-ci. La carte
 *  n'a pas à connaître les deux modèles pour dessiner les mêmes tuiles. */
export interface ClubDuSac {
    id: string;
    label: string;
    /** Sert au putter, seul club sans distance et seul à ne se comparer à
     *  aucun autre : il prend l'ardoise au lieu du papier chaud. */
    category: string;
    /** La distance annoncée, déjà mise en forme par l'appelant — c'est lui qui
     *  sait si le joueur compte en mètres ou en yards. */
    distance: string | null;
    /** La fiabilité, en pourcentage entier. `null` sous le premier coup. */
    taux: number | null;
    /** Une puce posée sur le coin de la tuile : l'ampoule du club suggéré sur
     *  le parcours, la clé du club à travailler au practice. */
    marque?: ReactNode;
    /** Un SECOND jeton, à l'autre coin.
     *
     *  Le conseil et le souvenir sont deux choses, et elles se contredisent
     *  souvent — c'est même là qu'elles sont le plus utiles ensemble : « on te
     *  propose le fer 7, mais la dernière fois tu as pris le fer 6 ». Les
     *  fondre en un seul jeton obligerait à choisir lequel taire. */
    memoire?: ReactNode;
}

interface Props {
    /** Le numéro de l'étape. Deux partout aujourd'hui, mais la carte n'a pas à
     *  le décider. */
    n: number;
    clubs: ClubDuSac[];
    /** Le club en cours, ou `null` avant le premier choix. */
    courantId: string | null;
    /** Ce qui s'écrit sous le nom du club : distance et fiabilité, en général.
     *  Une chaîne et non deux champs — le parcours et le practice n'y mettent
     *  pas tout à fait la même chose. */
    sousTitre: string;
    ouvert: boolean;
    onOuvrir: (ouvert: boolean) => void;
    onChoisir: (clubId: string) => void;
    disabled?: boolean;
    /** Le jeton à droite du titre.
     *
     *  Sur le parcours c'est « suggéré », qui dit pourquoi ce club-ci est
     *  proposé pour ce coup-ci. Au practice c'est « à travailler », qui dit que
     *  les dernières séances placent ce club sous votre moyenne. Deux avis, une
     *  place — et rien du tout quand il n'y a rien à dire. */
    action?: ReactNode;
    /** Ce qui suit « Choisir un club » dans la légende de la grille : la
     *  distance à couvrir, la liste des clubs à travailler… */
    legende?: string;
    /** L'outil, en PREMIÈRE TUILE de la grille — avant le driver.
     *
     *  Sur le parcours, c'est l'avis du prof : on lui donne une distance et il
     *  désigne un club. Au format d'une tuile parce que c'est un choix de la
     *  même famille : « avec quoi » commence souvent par « combien ». Et parce
     *  qu'en jeton de légende, la cible du pouce tombait à dix pixels de haut
     *  sur un écran qu'on touche avec un gant. */
    onOutil?: () => void;
    outilOuvert?: boolean;
    /** Sous la ligne du club, avant la grille. Le parcours y met l'écart au
     *  club idéal. */
    note?: ReactNode;
    /** Sous la grille, avant le bouton de fermeture : la raison d'une
     *  suggestion, un lien de correction. */
    sousLaGrille?: ReactNode;
}

/* L'icône du club : un fer stylisé, tête en bas à gauche.
 *
 *  Dessiné plutôt que pris dans une bibliothèque : aucune icône générique ne
 *  dit « club de golf » d'un coup d'œil à bout de bras, et une cible ou un
 *  drapeau diraient autre chose — la cible, c'est le trou ; le drapeau, c'est
 *  le green. Ici on parle de l'objet qu'on tient. */
export function ClubIcon({ size }: { size: number }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth={2} strokeLinecap="round"
            strokeLinejoin="round" aria-hidden="true">
            <path d="M7.5 21L16 5" />
            <path d="M16 5a2.1 2.1 0 1 1 3 3l-2 1.2-2.4-2.4L16 5z" />
        </svg>
    );
}

/* La carte « Choix du club » — parcours et practice, le même objet.
 *
 * Elle a d'abord été écrite pour le tour en cours, puis recopiée au practice
 * en « à peu près pareil » : icône différente, polices différentes, tuiles
 * plus grossières. Deux cartes qui se ressemblent sans se ressembler sont
 * pires que deux cartes franchement différentes — le joueur croit reconnaître
 * et se trompe de geste. Elles n'en font donc plus qu'une, et la seule chose
 * qui varie est ce que chaque écran a de particulier à dire : le jeton
 * d'en-tête, la légende de la grille, ce qui se glisse sous les tuiles.
 *
 * ── La ligne fermée ─────────────────────────────────────────────────────────
 *
 * Socle vert profond pour l'icône, nom en chiffres de tableau d'affichage,
 * distance et fiabilité en dessous, chevron à droite. Tout tient sur une
 * rangée : le chevron occupait autrefois une bande pleine largeur au bas de la
 * carte, une trentaine de pixels dont la seule fonction était de porter un
 * signe de six.
 *
 * Il pointe vers le bas et n'en bouge pas, sac ouvert compris : ce qui dit que
 * le sac est ouvert, c'est le sac lui-même, déplié en dessous sur la moitié de
 * l'écran. Une flèche qui se retourne serait un second témoin pour un fait
 * déjà évident.
 *
 * Trois boutons FRÈRES, jamais imbriqués — la ligne déplie le sac, le jeton
 * explique le choix, le chevron déplie aussi : un bouton dans un bouton est du
 * HTML invalide et une cible ambiguë.
 *
 * ── La grille ───────────────────────────────────────────────────────────────
 *
 * Trois par rangée et non quatre : à quatre, le taux de réussite ne tient plus
 * sous le nom, et c'est lui qui départage deux clubs voisins.
 */
export function ChoixDuClub({
    n, clubs, courantId, sousTitre, ouvert, onOuvrir, onChoisir,
    disabled = false, action, legende, note, sousLaGrille,
    onOutil, outilOuvert,
}: Props) {
    const courant = clubs.find((c) => c.id === courantId) ?? null;

    return (
        <Etape n={n} titre="Choix du club" action={action}>
            <div className="flex w-full items-center gap-2">
                <button type="button" disabled={disabled}
                    onClick={() => onOuvrir(!ouvert)}
                    className="flex min-w-0 flex-1 items-center gap-3 text-left">
                    <span className="flex size-10 shrink-0 items-center justify-center rounded-field bg-[var(--fairway-deep)] text-accent">
                        <ClubIcon size={20} />
                    </span>
                    <span className="min-w-0 flex-1">
                        {/* `leading-none` couperait les jambages.

                            Big Shoulders Display descend sous la ligne de base
                            d'environ un cinquième de cadratin, et une hauteur de
                            ligne de 1 donne une boîte d'exactement un cadratin :
                            le `g` de « Pitching wedge » passerait dessous, et
                            `truncate` — donc `overflow: hidden` — le raserait
                            net. 1,15 rend l'air qu'il faut sans desserrer la
                            ligne. */}
                        <span className="font-score block truncate text-2xl leading-[1.15]">
                            {courant?.label ?? 'Club'}
                        </span>
                        <span className="mt-0.5 block truncate text-xs font-semibold tabular-nums opacity-60">
                            {sousTitre}
                        </span>
                    </span>
                </button>
                <button type="button" disabled={disabled}
                    onClick={() => onOuvrir(!ouvert)}
                    aria-label={ouvert ? 'Fermer le sac' : 'Changer de club'}
                    aria-expanded={ouvert}
                    className="flex size-10 shrink-0 items-center justify-center rounded-field text-[color:var(--fairway-deep)] transition-colors hover:bg-base-200">
                    <ChevronDown size={24} aria-hidden="true" strokeWidth={2.5} />
                </button>
            </div>

            {note}

            {ouvert && (
                <div className="mt-3 border-t border-base-300 pt-3">
                    <p className="mb-2 truncate text-xs font-semibold opacity-60">
                        Choisir un club{legende ? ` · ${legende}` : ''}
                    </p>
                    <div className="grid grid-cols-3 gap-2">
                        {/* L'outil, AVANT le driver : la première tuile qu'on
                            touche, à la même taille que les autres. L'icône
                            seule — la légende au-dessus dit déjà la distance à
                            couvrir, et une tuile qui la répète est du bruit. */}
                        {onOutil && (
                            <button type="button" disabled={disabled}
                                onClick={onOutil}
                                aria-label="Donner la distance à couvrir"
                                aria-expanded={outilOuvert ?? false}
                                className="flex flex-col items-center justify-center rounded-field px-1 py-2.5 shadow-sm transition-transform active:scale-95"
                                style={{ background: 'var(--brass-ink)', color: 'var(--color-base-100)' }}>
                                <GraduationCap size={28} aria-hidden="true" />
                            </button>
                        )}
                        {clubs.map((c) => {
                            const actif = courantId === c.id;
                            /* Le putter se distingue par la TEINTE, pas par un
                               libellé : c'est le seul club sans distance et le
                               seul qui ne se compare à aucun autre. Une ardoise
                               froide au milieu des papiers chauds le fait
                               trouver sans le lire. */
                            const special = c.category === 'putter';
                            return (
                                <button key={c.id} type="button" disabled={disabled}
                                    onClick={() => onChoisir(c.id)}
                                    aria-pressed={actif}
                                    style={actif ? undefined : {
                                        backgroundColor: `var(${special ? '--tuile-speciale' : '--tuile'})`,
                                    }}
                                    className={`relative flex flex-col items-center gap-0.5 rounded-field px-1 py-2.5 shadow-sm transition-transform active:scale-95 ${
                                        actif ? 'bg-primary text-primary-content shadow-md' : ''
                                    }`}>
                                    {!actif && c.marque}
                                    {!actif && c.memoire}
                                    {/* Même raison que le nom du club en haut de
                                        la carte : « wedge », « hybride » ont un
                                        jambage à loger. */}
                                    <span className={`font-score leading-[1.15] ${c.label.length > 8 ? 'text-base' : 'text-lg'}`}>
                                        {c.label}
                                    </span>
                                    <span className={`text-xs font-bold leading-none tabular-nums ${
                                        actif ? 'opacity-85' : 'text-[color:var(--tuile-encre)]'}`}>
                                        {c.distance ?? '—'}
                                    </span>
                                    {/* Ce que le joueur fait VRAIMENT de ce club.
                                        La distance au-dessus est celle qu'il a
                                        déclarée ; le taux sort de ses balles. */}
                                    {c.taux != null && (
                                        <span className={`mt-1.5 w-full border-t pt-1.5 text-center text-[11px] font-bold leading-none tabular-nums ${
                                            actif
                                                ? 'border-primary-content/25 opacity-85'
                                                : 'border-black/[0.07] text-[color:var(--tuile-encre-pale)]'
                                        }`}>
                                            {c.taux} %
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                    </div>

                    {sousLaGrille}

                    {/* « Garder » referme sans rien changer : c'est une
                        proposition, jamais un passage obligé. Rien à garder tant
                        qu'aucun club n'est en cours. */}
                    {courant && (
                        <button type="button" onClick={() => onOuvrir(false)}
                            className="btn btn-accent mt-3 h-12 w-full gap-2 font-bold">
                            Garder le {courant.label}
                        </button>
                    )}
                </div>
            )}
        </Etape>
    );
}

/** Pourquoi ce club — le panneau laiton sous la grille.
 *
 *  Il n'existait que sur le parcours, et c'est justement la partie que le
 *  practice n'avait pas : la carte y montrait un club sans jamais dire ce qui
 *  le désigne. Les deux écrans ont pourtant une raison à donner — une distance
 *  qui tombe juste ici, une moyenne en baisse là — et c'est la même phrase à
 *  faire : « voici pourquoi celui-ci ».
 *
 *  Panneau dépliant et non fenêtre modale : la raison se lit À CÔTÉ des tuiles
 *  qu'elle départage. Une modale recouvrait précisément la grille qu'on était
 *  en train de comparer, et réclamait un « Compris » pour rendre l'écran —
 *  deux gestes pour une phrase.
 *
 *  L'ampoule et l'encre laiton : la couleur de ce qui est offert plutôt
 *  qu'exigé. Le composant ne fournit que le cadre et le titre ; ce qui est
 *  vrai de ce club-là, seul l'écran qui l'a calculé peut le dire. */
export function PourquoiCeClub({ label, children }: {
    label: string; children: ReactNode;
}) {
    return (
        <div className="mt-3 shrink-0 rounded-field bg-accent/25 p-3 text-[color:var(--brass-ink)]">
            <p className="flex items-center gap-1.5 text-sm font-bold">
                <Lightbulb size={14} aria-hidden="true" /> Pourquoi le {label} ?
            </p>
            {children}
        </div>
    );
}

/** Une ligne du panneau. Séparée du texte libre pour que les deux écrans
 *  espacent et graissent leurs phrases de la même façon. */
export function RaisonLigne({ children }: { children: ReactNode }) {
    return <p className="mt-1.5 text-xs font-semibold leading-relaxed">{children}</p>;
}

/** La puce d'un coin de tuile : ampoule du club suggéré sur le parcours, clé
 *  du club à travailler au practice.
 *
 *  Même cercle, même place, même taille, et même LAITON — c'est le glyphe qui
 *  dit de quoi il s'agit, pas la couleur. Le practice avait un cercle ambre :
 *  deux teintes pour le même rôle, c'est exactement l'écart qu'on retire. */
export function MarqueTuile({ children }: { children: ReactNode }) {
    return (
        <span className="absolute -right-1.5 -top-1.5 flex size-6 items-center justify-center rounded-full bg-accent text-accent-content shadow-md">
            {children}
        </span>
    );
}

/* LA PASTILLE DU DERNIER PASSAGE, au coin opposé à celui du conseil.
 *
 *  À gauche pour ne jamais se disputer la place avec l'ampoule : les deux
 *  s'affichent ensemble dès que le club joué la dernière fois n'est pas celui
 *  qu'on propose, et c'est précisément le cas intéressant.
 *
 *  Sa couleur est celle de l'ENDROIT ATTEINT ce jour-là — vert clair pour le
 *  green, sable pour le bunker, brique pour le hors-limites. Ce sont les mêmes
 *  teintes que celles qu'on touche en saisissant un coup : rien de neuf à
 *  apprendre. Elle remplace une phrase de deux lignes sous la tuile, sur un
 *  écran qu'on lit debout entre deux coups.
 *
 *  Plus large que le jeton du conseil, parce qu'un aplat de couleur a besoin de
 *  surface pour se nommer, là où une icône se reconnaît à sa forme.
 *
 *  La couleur ne dit rien SEULE pour autant : l'horloge à l'intérieur dit
 *  « déjà joué » quelle que soit la teinte, et le nom de la zone part dans
 *  l'étiquette d'accessibilité. */
export function MarqueMemoire({ landing }: { landing: string | null }) {
    const connue = landing != null
        && (SHOT_LANDING_LABELS as Record<string, string>)[landing] != null;
    const mot = connue ? SHOT_LANDING_LABELS[landing as ShotLanding] : null;
    return (
        <span
            aria-label={mot ? `Déjà joué ici — ${mot}` : 'Déjà joué ici'}
            title={mot ?? undefined}
            className="absolute -left-2 -top-2 flex size-8 items-center justify-center rounded-full shadow-md ring-2 ring-base-100"
            style={connue
                ? { background: `var(--zone-${landing})`, color: `var(--zone-${landing}-encre)` }
                : { background: 'var(--brass-ink)', color: 'var(--color-base-100)' }}>
            <History size={15} aria-hidden="true" />
        </span>
    );
}
